import { DeviceType } from 'src/constants/DeviceTypes';
import { Device, DeviceAllocation } from 'src/store/sageAdminApi';

/** One device paired with the allocation it was pulled out of. */
export interface DeviceWithAllocation {
    device: Device;
    allocation: DeviceAllocation;
}

// Internal utility to extract devices along with their allocations
function extractDevicesFromAllocations(
    allocations: DeviceAllocation[] = []
): DeviceWithAllocation[] {
    return allocations.flatMap(
        (allocation) =>
            allocation.devices?.map((device) => ({
                device,
                allocation,
            })) || []
    );
}

export function getDevicesFromAllocation(
    allocation?: DeviceAllocation,
    deviceType?: DeviceType,
    deviceRid?: string
) {
    const devicesWithAllocations = extractDevicesFromAllocations(allocation ? [allocation] : []);
    const isMultiDeviceAllocation = allocation?.allocationType === 'OFFLINE_DOOR';

    const deviceWithAllocation = deviceRid
        ? devicesWithAllocations.find(({ device }) => device?.id === deviceRid)
        : devicesWithAllocations.length === 1
        ? devicesWithAllocations[0]
        : undefined;

    return {
        device: deviceWithAllocation?.device,
        devices: devicesWithAllocations.map(({ device }) => device),
        devicesWithAllocations,
        deviceWithAllocation,
        isMultiDeviceAllocation,
        devicesOfDeviceType: deviceType
            ? devicesWithAllocations.filter(({ device }) => device?.type === deviceType)
            : devicesWithAllocations,
    };
}

export function getDevicesFromAllocations(
    allocations: DeviceAllocation[] = [],
    deviceType?: DeviceType
) {
    const devicesWithAllocations = extractDevicesFromAllocations(allocations);

    const hasDoorConfigurationThatNeedsSensors =
        allocations.some(
            (allocation) =>
                allocation.allocationType === 'OFFLINE_DOOR' || allocation.allocationType === 'DOOR'
        ) || allocations.length === 0;

    const devicesOfDeviceType = deviceType
        ? devicesWithAllocations.filter(({ device }) => device?.type === deviceType)
        : devicesWithAllocations;

    return {
        devicesWithAllocations,
        devices: devicesWithAllocations.map(({ device }) => device),
        devicesOfDeviceType,
        hasDoorConfigurationThatNeedsSensors,
    };
}
